import { Paper } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import { DashboardInput } from '../utils/DashboardInput'
import { DashboardPercent } from '../utils/DashboardPercent'
import logo1 from '../../../assets/image/dashboard1.png'
import logo2 from '../../../assets/image/dashboard4.png'
import ProvinceService from '../../provinces/services/Provinces.js'
import Controls from '../../../components/Entry/Controls.js'
import AgroDashboardService from '../service/AgroDashboard.js'
import { Context } from '../../nursery/components/youngPlant/utils/hooks/NurseryDataContext.jsx'
import DataSetsService from '../../datasets/services/DataSets.js'

const useStyles = makeStyles(() => ({
    pageContent: {
        margin: 10,
        backgroundColor: '#F2F8F6',
        '& .MuiPaper-root': { border: 'none', boxShadow: 'none' }
    }
}))

const TablePaper = styled(Paper)(() => ({
    boxShadow: '0px 5px 5px -3px #d2d2d2,0px 8px 10px 1px #d2d2d2,0px 3px 14px 2px #d2d2d2'
}))

const DEFAULT_VALUES = {
    site: '',
    nursery: '',
    species: '',
    project_type: '',
    year: new Date().getFullYear()
}

export const NurseryDashboard = ({ needLoad, setNeedLoad }) => {
    const classes = useStyles()
    const dataContext = useContext(Context)

    const [sites, setSites] = useState([])
    const [nurseries, setNurseries] = useState([])
    const [allNurseries, setAllNurseries] = useState([])
    const [species, setSpecies] = useState([])
    const [summary, setSummary] = useState({
        total_sown: 0,
        total_target: 0,
        total_plant_remaining: 0
    })
    const [siteChangedByNursery, setSiteChangedByNursery] = useState(false)

    const [projectTypes, setProjectTypes] = useState([]) // <-- options du select "Type de projet"

    const [values, setValues] = useState({ ...DEFAULT_VALUES })

    // ── 1. Charger les sites ──────────────────────────────────────
    useEffect(() => {
        setNeedLoad(true)
        ProvinceService.getAgroSites()
            .then((res) => {
                if (res?.data) {
                    setSites(res.data)
                }
            })
            .finally(() => setNeedLoad(false))
    }, [])

    // ── 1bis. Charger les types de projet (project_type) ──────────
    useEffect(() => {
        DataSetsService.getDataCode('project_type')
            .then((res) => {
                const raw = res?.data?.[0]?.database

                if (!raw) {
                    setProjectTypes([])
                    return
                }

                try {
                    const parsed = JSON.parse(raw)
                    setProjectTypes(Array.isArray(parsed) ? parsed : [])
                } catch (e) {
                    console.error('Erreur parsing project_type database:', e)
                    setProjectTypes([])
                }
            })
            .catch((err) => console.error('Erreur chargement project_type:', err))
    }, [])

    // ── 2. Charger TOUTES les nurseries au montage ────────────────
    useEffect(() => {
        AgroDashboardService.listNursery().then((data) => setAllNurseries(data))
    }, [])

    // ── 3. Charger les nurseries selon site ───────────────────────
    useEffect(() => {
        if (!values.site) {
            setNurseries([])
            setValues((prev) => ({ ...prev, nursery: '', species: '' }))
            return
        }

        setNeedLoad(true)
        AgroDashboardService.listNursery(values.site)
            .then((data) => {
                setNurseries(data)

                // Si le site a été changé par sélection d'une nursery
                // → on ne réinitialise pas la nursery
                if (siteChangedByNursery) {
                    setSiteChangedByNursery(false)
                    return
                }

                setValues((prev) => ({
                    ...prev,
                    nursery: data.length > 0 ? data[0].id : '',
                    species: ''
                }))
            })
            .finally(() => setNeedLoad(false))
    }, [values.site])

    // ── 4. Charger les espèces selon nursery ──────────────────────
    useEffect(() => {
        if (!values.nursery) {
            setSpecies([])
            setValues((prev) => ({ ...prev, species: '' }))
            return
        }

        setNeedLoad(true)
        AgroDashboardService.getSpecies(values.nursery)
            .then((data) => {
                setSpecies(data)
                setValues((prev) => ({
                    ...prev,
                    species: data.length > 0 ? data[0].id : ''
                }))
            })
            .finally(() => setNeedLoad(false))
    }, [values.nursery])

    // ── 5. Charger le dashboard selon tous les filtres ────────────
    // Aucun filtre requis : quand tout est vide (ex. après réinitialisation),
    // le dashboard se charge sans restriction (toutes données confondues).
    useEffect(() => {
        setNeedLoad(true)
        AgroDashboardService.getDashboard({
            year: values.year,
            site_id: values.site || undefined,
            nursery_id: values.nursery || undefined,
            specie_id: values.species || undefined,
            project_type: values.project_type || undefined
        })
            .then((data) => setSummary(data.summary))
            .finally(() => setNeedLoad(false))
    }, [values.year, values.site, values.nursery, values.species, values.project_type])

    // ── handleChange ──────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target

        // Sélection d'une nursery → met à jour le site automatiquement
        if (name === 'nursery' && value) {
            const found = allNurseries.find((n) => n.id === value)
            if (found && found.site_id !== values.site) {
                setSiteChangedByNursery(true)
                setValues((prev) => ({
                    ...prev,
                    nursery: value,
                    site: found.site_id,
                    species: ''
                }))
                return
            }
        }

        setValues((prev) => ({ ...prev, [name]: value }))
    }

    // ── handleReset ───────────────────────────────────────────────
    // Vide tous les filtres (site, pépinière, espèce, type de projet) ;
    // seule l'année reste à sa valeur par défaut (année courante).
    const handleReset = () => {
        setSiteChangedByNursery(false)
        setValues({ ...DEFAULT_VALUES })
    }

    const getYears = () => {
        const current = new Date().getFullYear()
        return Array.from({ length: 9 }, (_, i) => current - 4 + i)
    }

    return (
        <Paper className={classes.pageContent} elevation={0}>
            <TablePaper style={{ padding: 10, marginTop: 20, borderRadius: 10 }} elevation={8}>
                <div>
                    {/* Header */}
                    <div className="flex justify-between">
                        <p className="font-semibold text-lg">Pépinière</p>
                        <select
                            className="bg-[#0FCB8F] w-24 h-8 rounded-md border-none text-white"
                            name="year"
                            value={values.year}
                            onChange={handleChange}
                        >
                            {getYears().map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Filtres */}
                    <div className="flex items-end gap-[40px] mb-[15px] w-[80%] mt-[16px]">
                        <div className="w-full">
                            <Controls.AutoCompleteSelect
                                label="Site"
                                name="site"
                                value={values.site || ''}
                                onChange={handleChange}
                                options={sites}
                                labelKey="name"
                                valueKey="id"
                            />
                        </div>
                        <div className="w-full">
                            <Controls.AutoCompleteSelect
                                label="Pépinière"
                                name="nursery"
                                value={values.nursery || ''}
                                onChange={handleChange}
                                options={allNurseries}
                                labelKey="name"
                                valueKey="id"
                            />
                        </div>
                        <div className="w-full">
                            <Controls.AutoCompleteSelect
                                label="Espèces"
                                name="species"
                                value={values.species || ''}
                                onChange={handleChange}
                                options={species}
                                labelKey="name"
                                valueKey="id"
                            />
                        </div>
                        {/* <div className="w-full">
                            <Controls.AutoCompleteSelect
                                label="Type de reboisement"
                                name="project_type"
                                value={values.project_type || ''}
                                onChange={handleChange}
                                options={projectTypes}
                                labelKey="label"
                                valueKey="label"
                            />
                        </div> */}
                        <button
                            type="button"
                            onClick={handleReset}
                            className="shrink-0 h-10 px-4 rounded-md border border-[#0FCB8F] text-[#0FCB8F] bg-white hover:bg-[#0FCB8F] hover:text-white transition-colors"
                        >
                            Réinitialiser
                        </button>
                    </div>

                    {/* Données */}
                    <div className="flex gap-[20px] w-[75%]">
                        <DashboardInput title="Objectif" content={`${summary?.total_target || 0} de jeunes plants.`} />
                        <DashboardInput
                            title="Nombre de plants semés"
                            showImg={true}
                            textColor="text-green-500"
                            src={logo1}
                            value={summary?.total_sown || 0}
                        />
                        <DashboardInput
                            title="Nombre de plants disponibles"
                            showImg={true}
                            textColor="text-green-500"
                            src={logo2}
                            value={summary?.total_plant_remaining || 0}
                        />
                    </div>

                    <div className="flex justify-between gap-[20px] mt-[40px] mb-[20px]">
                        <DashboardPercent text="Taux de germination" value={summary?.germination_rate || 0} />
                        <DashboardPercent text="Taux de réussite" value={summary?.success_rate || 0} />
                        <DashboardPercent text="Taux de survie" value={summary?.survival_rate || 0} />
                    </div>
                </div>
            </TablePaper>
        </Paper>
    )
}
